"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "use-debounce";

import { useGetSellerOptionsQuery } from "@/store/sellerOptionsApi";
import {
  sellerOptionsDeliveryZipCodeChanged,
  sellerOptionsFilterToggled,
  sellerOptionsFiltersCleared,
  sellerOptionsQuantityChanged,
  sellerOptionsSortChanged,
  type SellerOptionsPage,
} from "@/store/sellerOptionsSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  getVisibleOffers,
  type SellerOptionContext,
  type SellerOptionFilter,
  type SellerOptionSort,
} from "./sellerOptions";

/** Quick +/- taps settle before the offers are re-priced. */
const QUANTITY_DEBOUNCE_MS = 400;

/**
 * The product page's offers at the shopper's quantity and delivery zip, with the filters and sort they picked.
 * Shared through the store so the table, the seller options drawer, and the buy box agree. `renderedPage` is what the page
 * was server-rendered with; it stands in until the store has it, so the first render matches the server's.
 */
export default function useSellerOptions(renderedPage?: SellerOptionsPage): SellerOptions | null {
  const dispatch = useAppDispatch();
  const state = useAppSelector((appState) => appState.sellerOptionsSlice);
  const storeHasPage = state.page !== null && (renderedPage === undefined || state.page.mpId === renderedPage.mpId);
  const page = storeHasPage ? state.page : (renderedPage ?? null);

  const quantity = storeHasPage ? state.quantity : (page?.quantity ?? 1);
  const filters = useMemo(() => new Set(storeHasPage ? state.filters : []), [storeHasPage, state.filters]);
  const sort: SellerOptionSort = storeHasPage ? state.sort : "recommended";
  const deliveryZipCode = (storeHasPage ? state.deliveryZipCode : null) ?? page?.deliveryZipCode ?? null;

  // The debounced quantity names its product, so a quantity picked on one product never prices another.
  const [debounced] = useDebounce({ mpId: page?.mpId, quantity }, QUANTITY_DEBOUNCE_MS, { equalityFn: isSamePick });
  const pricedQuantity = page !== null && debounced.mpId === page.mpId ? debounced.quantity : (page?.quantity ?? 1);
  const needsReprice = page !== null && (pricedQuantity !== page.quantity || deliveryZipCode !== page.deliveryZipCode);
  const { currentData, isFetching, isError } = useGetSellerOptionsQuery(
    {
      mpId: page?.mpId ?? 0,
      quantity: pricedQuantity,
      isManufacturerSponsor: page?.isManufacturerSponsor ?? false,
      deliveryZipCode,
    },
    { skip: !needsReprice },
  );

  // When re-pricing fails, go back to the last quantity and zip the offers were priced for, so the prices shown
  // always match the quantity and zip shown.
  const lastPricedQuantity = useRef(page?.quantity ?? 1);
  const lastPricedZipCode = useRef(deliveryZipCode);
  useEffect(() => {
    if (!needsReprice || currentData !== undefined) {
      lastPricedQuantity.current = pricedQuantity;
      lastPricedZipCode.current = deliveryZipCode;
    } else if (isError) {
      dispatch(sellerOptionsQuantityChanged(lastPricedQuantity.current));
      dispatch(sellerOptionsDeliveryZipCodeChanged(lastPricedZipCode.current));
    }
  }, [currentData, deliveryZipCode, dispatch, isError, needsReprice, pricedQuantity]);

  const vendorPurchases = page?.vendorPurchases;
  // Fixed for the life of the page, so an offer can't drift in or out of "expiring soon" between renders.
  const now = useMemo(() => new Date(), []);
  const context = useMemo<SellerOptionContext>(
    () => ({ vendorPurchases: new Map(vendorPurchases?.map((purchase) => [purchase.vendorId, purchase])), now }),
    [vendorPurchases, now],
  );

  // While a new quantity loads, this product's last offers stay up under the loading overlay.
  const [lastRepriced, setLastRepriced] = useState<{ mpId: number; offers: VendorOptionState[] } | null>(null);
  useEffect(() => {
    if (page !== null && needsReprice && currentData !== undefined) {
      setLastRepriced({ mpId: page.mpId, offers: currentData });
    }
  }, [page, needsReprice, currentData]);
  const offers = useMemo(() => {
    if (page === null) {
      return [];
    }
    if (!needsReprice) {
      return page.offers;
    }
    return currentData ?? (lastRepriced?.mpId === page.mpId ? lastRepriced.offers : page.offers);
  }, [page, needsReprice, currentData, lastRepriced]);
  const visibleOffers = useMemo(
    () => getVisibleOffers(offers, filters, sort, context),
    [offers, filters, sort, context],
  );

  if (page === null) {
    return null;
  }

  return {
    page,
    quantity,
    offers,
    visibleOffers,
    filters,
    sort,
    context,
    deliveryZipCode,
    isUpdating: quantity !== pricedQuantity || isFetching,
    setQuantity: (next) => dispatch(sellerOptionsQuantityChanged(next)),
    setDeliveryZipCode: (next) => dispatch(sellerOptionsDeliveryZipCodeChanged(next)),
    toggleFilter: (filter) => dispatch(sellerOptionsFilterToggled(filter)),
    clearFilters: () => dispatch(sellerOptionsFiltersCleared()),
    setSort: (next) => dispatch(sellerOptionsSortChanged(next)),
  };
}

function isSamePick(a: QuantityPick, b: QuantityPick): boolean {
  return a.mpId === b.mpId && a.quantity === b.quantity;
}

interface QuantityPick {
  mpId: number | undefined;
  quantity: number;
}

export interface SellerOptions {
  page: SellerOptionsPage;
  /** The quantity the shopper picked. */
  quantity: number;
  /** Every in-stock offer, in neo's recommended order. */
  offers: VendorOptionState[];
  /** The offers that pass the active filters, in the chosen order. */
  visibleOffers: VendorOptionState[];
  filters: ReadonlySet<SellerOptionFilter>;
  sort: SellerOptionSort;
  context: SellerOptionContext;
  /** The zip the offers are priced for, or null when the server couldn't resolve one. */
  deliveryZipCode: string | null;
  /** The offers are being re-priced for a new quantity or zip. */
  isUpdating: boolean;
  setQuantity: (quantity: number) => void;
  setDeliveryZipCode: (zipCode: string) => void;
  toggleFilter: (filter: SellerOptionFilter) => void;
  clearFilters: () => void;
  setSort: (sort: SellerOptionSort) => void;
}
